import React, { useState } from 'react';

// Reply all, Follow up and a new email on an audit's Correspondence,
// written here and sent by the platform (operator order 2026-11-03: one
// place per audit, every email and every reply saved, and the sender told
// whether it went out). Inline on the page, never a modal. The email being
// answered shows in full beneath the editor, formatting kept, never cut, and
// goes out whole under the new words. The send goes from the reader's own
// mailbox when they have connected it, else from the compliance mailbox in
// their name; either way it is on the record the moment it is sent, or
// marked failed with the reason. The audit's reply address rides every
// send, so every answer files here too.
//
//   mode       'reply' | 'follow_up' | 'new'
//   to         the suggested To line (everyone on the email being answered)
//   subject    the suggested subject
//   prior      the correspondence row being answered (shown in full), or null
//   draftHref  the unsent .eml for the reader's own mail program, or null
//   onSend     async ({ mode, to, cc, subject, body, files }) => void; throws to show the error
//   onClose    () => void
export default function EmailReplyComposer({ mode, to = [], subject = '', prior = null, draftHref = null, onSend, onClose }) {
  const [toLine, setToLine] = useState((to || []).join(', '));
  const [ccLine, setCcLine] = useState('');
  const [subj, setSubj] = useState(subject || '');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const split = (s) => String(s || '').split(/[,;]/).map((x) => x.trim()).filter((x) => x.includes('@'));
  const field = 'w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-orange-300';
  const title = mode === 'follow_up' ? 'Follow up' : mode === 'reply' ? 'Reply all' : 'New email';
  const send = async () => {
    setErr('');
    if (!split(toLine).length) { setErr('Add at least one address on the To line.'); return; }
    if (!body.trim()) { setErr('Write the message.'); return; }
    setSending(true);
    try {
      await onSend({ mode, to: split(toLine), cc: split(ccLine), subject: subj.trim(), body: body.trim(), files });
      onClose();
    } catch (e) {
      setErr(e?.message || 'Could not send.');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-2 border border-orange-200 bg-orange-50/40 rounded-lg px-3 py-2.5 space-y-1.5" data-testid="email-reply-composer">
      <div className="flex items-center gap-2">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-orange-700">{title}</div>
        {draftHref && (
          <a href={draftHref} className="ml-auto text-[10.5px] font-semibold text-slate-500 hover:text-slate-800 no-underline"
            title="Opens the same email as an unsent draft in your own mail program. It is on the record as opened there, and becomes the email as sent when your copy reaches the audit address.">
            Prefer your mail program? Open it there
          </a>
        )}
      </div>
      <label className="block text-[10.5px] text-slate-500">To
        <input className={field} value={toLine} onChange={(e) => setToLine(e.target.value)} placeholder="name@example.com, other@example.com" />
      </label>
      <label className="block text-[10.5px] text-slate-500">Cc
        <input className={field} value={ccLine} onChange={(e) => setCcLine(e.target.value)} placeholder="Optional. The compliance officers are copied by the rule." />
      </label>
      <label className="block text-[10.5px] text-slate-500">Subject
        <input className={field} value={subj} onChange={(e) => setSubj(e.target.value)} />
      </label>
      <label className="block text-[10.5px] text-slate-500">Message
        <textarea className={`${field} min-h-[120px]`} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={prior ? 'Your message. The email below follows it in full, exactly as it was sent.' : 'Your message.'} />
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-orange-400 text-[11px] font-semibold cursor-pointer bg-white">
          Attach files{files.length ? ` · ${files.length}` : ''}
          <input type="file" multiple className="hidden" onChange={(e) => { setFiles([...files, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
        </label>
        {files.map((f, i) => (
          <span key={i} className="text-[10.5px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{f.name}
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="ml-1 text-slate-400 hover:text-rose-600">✕</button>
          </span>
        ))}
      </div>
      <div className="text-[10.5px] text-slate-500">
        Sent from your own mailbox when it is connected, else from the compliance mailbox in your name. It is saved here the moment it goes out, or marked failed with the reason. The audit's reply address rides the email, so every answer is saved here too.
      </div>
      {err && <div className="text-[11px] text-red-700">{err}</div>}
      <div className="flex items-center gap-2 pt-0.5">
        <button type="button" onClick={send} disabled={sending}
          className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">{sending ? 'Sending…' : 'Send'}</button>
        <button type="button" onClick={onClose} disabled={sending} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
      </div>
      {prior && (
        <div className="pt-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">The email you are answering, in full. It goes out beneath your message.</div>
          {prior.body_html
            ? <iframe title="The email being answered" sandbox="" srcDoc={prior.body_html} className="w-full bg-white border border-slate-200 rounded-lg" style={{ height: 360, border: 0 }} />
            : <div className="whitespace-pre-wrap text-[11px] text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2 max-h-72 overflow-y-auto">{prior.body || ''}</div>}
        </div>
      )}
    </div>
  );
}
